import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowUpRight, BellPlus, Check, FileText } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { PriceChangedBadge } from "@/components/PriceChangedBadge";
import { cn } from "@/lib/utils";

const MotionLink = motion.create(Link);

function useTilt(ref) {
  const rx = useSpring(useMotionValue(0), { stiffness: 220, damping: 22 });
  const ry = useSpring(useMotionValue(0), { stiffness: 220, damping: 22 });
  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    ref.current.style.setProperty("--mx", `${e.clientX - r.left}px`);
    ref.current.style.setProperty("--my", `${e.clientY - r.top}px`);
    if (window.matchMedia("(hover: none)").matches) return;
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 8);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 6);
  };
  const onLeave = () => { rx.set(0); ry.set(0); };
  return { onMove, onLeave, style: { rotateX: rx, rotateY: ry, transformPerspective: 1000 } };
}

const SelectDot = ({ brand, selected }) => (
  <span data-testid={`brand-select-indicator-${brand.slug}`} className={cn(
    "absolute right-5 top-5 z-10 flex h-8 w-8 items-center justify-center rounded-full border-2 transition-[background-color,border-color,transform] duration-300",
    selected ? "scale-110 border-ph-lime bg-ph-lime text-ph-navy" : "border-ph-line bg-ph-surface text-ph-muted group-hover:border-ph-accent",
  )}>
    {selected ? <Check className="h-4 w-4" /> : <BellPlus className="h-3.5 w-3.5" />}
  </span>
);

export function BrandCard({ brand, selectable = false, selected = false, onToggle, view = "grid" }) {
  const ref = useRef(null);
  const tilt = useTilt(ref);
  const cats = brand?.categories ?? [];
  const Tag = selectable ? motion.button : MotionLink;
  const tagProps = selectable ? { type: "button", onClick: () => onToggle?.(brand), "aria-pressed": selected } : { to: `/brands/${brand.slug}` };
  const frame = cn(
    "ph-spot group relative flex w-full overflow-hidden border bg-ph-surface text-left transition-[box-shadow,border-color,background-color] duration-500 hover:shadow-[0_30px_60px_-30px_rgb(var(--ph-glow)/0.4)]",
    selected ? "border-ph-lime ring-2 ring-ph-lime/40" : "border-ph-line hover:border-ph-accent/40",
  );

  if (view === "list") {
    return (
      <Tag ref={ref} {...tagProps} data-testid={`brand-card-${brand.slug}`} className={cn(frame, "items-center gap-4 rounded-3xl p-4 pr-14 sm:gap-6 sm:p-5 sm:pr-16")}>
        {selectable && <SelectDot brand={brand} selected={selected} />}
        <BrandLogo brand={brand} className="h-12 w-12 shrink-0" textClass="text-sm" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-xl font-extrabold tracking-tight text-ph-ink">{brand.name}</h3>
            {brand.price_changed && <PriceChangedBadge testId={`brand-price-changed-${brand.slug}`} />}
          </div>
          <p className="truncate text-sm text-ph-muted">{cats.join(" · ") || "Electrical"}</p>
        </div>
        <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-ph-raised px-3 py-1 font-mono-ph text-[11px] text-ph-muted sm:flex">
          <FileText className="h-3 w-3" /> {brand.doc_count ?? 0} {brand.doc_count === 1 ? "list" : "lists"}
        </span>
        {!selectable && <ArrowUpRight className="absolute right-5 h-5 w-5 text-ph-muted transition-transform duration-300 group-hover:rotate-45 group-hover:text-ph-accent" />}
      </Tag>
    );
  }

  return (
    <Tag ref={ref} {...tagProps} onMouseMove={tilt.onMove} onMouseLeave={tilt.onLeave} style={tilt.style}
      data-testid={`brand-card-${brand.slug}`} className={cn(frame, "h-full flex-col rounded-[1.75rem] p-6")}>
      {selectable && <SelectDot brand={brand} selected={selected} />}
      <div className={cn("flex items-start justify-between", selectable && "pr-10")}>
        <BrandLogo brand={brand} />
        <span className="flex items-center gap-1.5 rounded-full bg-ph-raised px-3 py-1 font-mono-ph text-[11px] text-ph-muted">
          <FileText className="h-3 w-3" /> {brand.doc_count ?? 0} {brand.doc_count === 1 ? "list" : "lists"}
        </span>
      </div>
      {brand.price_changed && <PriceChangedBadge className="mt-5 self-start" testId={`brand-price-changed-${brand.slug}`} />}
      <h3 className={cn("font-display text-2xl font-extrabold tracking-tight text-ph-ink", brand.price_changed ? "mt-3" : "mt-6")}>{brand.name}</h3>
      <p className="mt-1.5 text-sm text-ph-muted">
        {cats[0] || "Electrical"}
        {cats.length > 1 && <span> · +{cats.length - 1} more</span>}
      </p>
      <div className="mt-auto pt-8">
        {selectable ? (
          <span className={cn("inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-300",
            selected ? "bg-ph-lime text-ph-navy" : "border border-ph-line text-ph-ink group-hover:border-ph-accent group-hover:text-ph-accent")}>
            {selected ? <><Check className="h-4 w-4" /> Selected</> : <><BellPlus className="h-4 w-4" /> Add to alerts</>}
          </span>
        ) : (
          <span data-testid={`brand-card-view-${brand.slug}`}
            className="inline-flex items-center gap-2 rounded-full border border-ph-line px-4 py-2 text-sm font-medium text-ph-ink transition-colors duration-300 group-hover:border-ph-lime group-hover:bg-ph-lime group-hover:text-ph-navy">
            View Price Lists
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </span>
        )}
      </div>
    </Tag>
  );
}
